import { utils, writeFile } from "xlsx";
import { getSystemFormattedTimestamp } from "../../../utils/dateFormatted";
import { logger } from "../../../utils/logger";
import { normalizeReceiptNoForDisplay } from "../../../utils/receiptNoFormatter";

/**
 * Filters donations based on search criteria.
 * @param {Array} donations - List of donations.
 * @param {Object} searchFilters - Search criteria.
 * @returns {Array} Filtered donations.
 */

export const filterDonations = (donations, searchFilters) => {
  if (!Array.isArray(donations)) return [];
  return donations.filter((donation) => {
    if (!donation || typeof donation !== "object") return false;

    // Donor name filter
    const donorName = `${donation.firstName || ""} ${
      donation.lastName || ""
    }`.toLowerCase();
    const filterName = (searchFilters.name || "").toLowerCase();

    // Global search filter
    const globalSearch = (searchFilters.globalSearch || "").toLowerCase();

    // Apply all filters with AND logic
    if (
      (searchFilters.donorId &&
        Number(donation.donorId) !== Number(searchFilters.donorId)) ||
      (searchFilters.name && !donorName.includes(filterName)) ||
      (searchFilters.mobile &&
        !(donation.mobile1 || "")
          .toLowerCase()
          .includes(searchFilters.mobile.toLowerCase()) &&
        !(donation.mobile2 || "")
          .toLowerCase()
          .includes(searchFilters.mobile.toLowerCase())) ||
      (searchFilters.bookNo &&
        String(donation.bookNo || "").toLowerCase() !==
          String(searchFilters.bookNo).toLowerCase()) ||
      (searchFilters.receiptNo &&
        String(donation.receiptNo || "").toLowerCase() !==
          String(searchFilters.receiptNo).toLowerCase()) ||
      (searchFilters.transactionId &&
        !String(donation.transactionId || "")
          .toLowerCase()
          .includes(String(searchFilters.transactionId).toLowerCase())) ||
      (searchFilters.remarks &&
        !(donation.donationRemarks || "")
          .toLowerCase()
          .includes((searchFilters.remarks || "").toLowerCase())) ||
      (globalSearch &&
        !(
          donorName.includes(globalSearch) ||
          String(donation.donorId || "")
            .toLowerCase()
            .includes(globalSearch) ||
          String(donation.receiptNo || "")
            .toLowerCase()
            .includes(globalSearch) ||
          String(donation.bookNo || "")
            .toLowerCase()
            .includes(globalSearch) ||
          (donation.mobile1 || "").toLowerCase().includes(globalSearch) ||
          (donation.mobile2 || "").toLowerCase().includes(globalSearch) ||
          (donation.city || "").toLowerCase().includes(globalSearch) ||
          (donation.payMode || "").toLowerCase().includes(globalSearch) ||
          (donation.remarks || "").toLowerCase().includes(globalSearch) ||
          (donation.donationRemarks || "").toLowerCase().includes(globalSearch)
        ))
    ) {
      return false;
    }
    return true;
  });
};
/**
 * Exports donations to an Excel file.
 * @param {Array} donations - List of donations.
 */
export const exportDonationsToExcel = (donations, user = {}) => {
  try {
    if (!Array.isArray(donations)) return;

    const formatDate = (date) =>
      date ? new Date(date).toISOString().split("T")[0] : "-";

    const exportData = donations.map((donation) => ({
      "Transaction ID": donation.transactionId || "",
      "Donor ID": donation.donorId || "",
      "First Name": donation.firstName || "",
      "Last Name": donation.lastName || "",
      City: donation.city || "",
      Mobile1: donation.mobile1 || "",
      Mobile2: donation.mobile2 || "",
      "Book No": donation.bookNo || "",
      "Receipt No": normalizeReceiptNoForDisplay(donation.receiptNo, donation.receiptDate) || "",
      "Child Receipt No": donation.childReceiptNo || "",
      "Actual Receipt No": donation.actualReceiptNo || "",
      "Receipt Date": formatDate(donation.receiptDate),
      Amount: donation.amount || "",
      "Consolidated Amount": donation.consolidatedAmount || donation.amount || "",
      "Pay Mode": donation.payMode || "",
      "Bank Name": donation.bankName || "",
      "Instrument No": donation.instrumentNo || "",
      "Instrument Date": formatDate(donation.instrumentDate),
      "UPI-TNXId": donation.upiId || "",
      "Pay Purpose": donation.payPurpose || "",
      "Donation Remarks": donation.donationRemarks || "",
    }));

    const worksheet = utils.json_to_sheet(exportData, {
      skipHeader: false,
      dense: false
    });
    
    // Set column widths for better readability
    const colWidths = [
      { wch: 15 }, // Transaction ID
      { wch: 10 }, // Donor ID
      { wch: 15 }, // First Name
      { wch: 15 }, // Last Name
      { wch: 12 }, // City
      { wch: 12 }, // Mobile1
      { wch: 12 }, // Mobile2
      { wch: 10 }, // Book No
      { wch: 12 }, // Receipt No
      { wch: 15 }, // Child Receipt No
      { wch: 12 }, // Receipt Date
      { wch: 10 }, // Amount
      { wch: 15 }, // Consolidated Amount
      { wch: 12 }, // Pay Mode
      { wch: 15 }, // Bank Name
      { wch: 15 }, // Instrument No
      { wch: 12 }, // Instrument Date
      { wch: 15 }, // UPI-TNXId
      { wch: 15 }, // Pay Purpose
      { wch: 20 }, // Donation Remarks
    ];
    worksheet['!cols'] = colWidths;
    
    const workbook = utils.book_new();
    utils.book_append_sheet(
      workbook,
      worksheet,
      `Donations_${getSystemFormattedTimestamp()}`
    );
    writeFile(
      workbook,
      `donations_export_${getSystemFormattedTimestamp()}.xlsx`,
      { compression: true }
    );
    logger.info("Donations exported to Excel", {
      username: user?.username,
      role: user?.role,
      count: donations.length,
    });
  } catch (error) {
    logger.error("Error exporting donations to Excel", {
      error: error,
      username: user?.username,
      role: user?.role,
    });
  }
};
